import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import {
  User,
  MapPin,
  Stethoscope,
  FileText,
  Calendar,
  Phone,
  Video,
  Hash,
  Activity,
  CalendarClock,
  Trash2,
  Loader2,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { SheetActions, SheetBody } from '@/components/custom/sheet-layout';
import { cn } from '@/lib/utils';
import { getAppointmentClient } from '@/lib/api-clients';
import { formatPhone } from '@/lib/format';
import { queryKeys } from '@/lib/query-keys';
import { CANCEL_REASON_MAX_CHARS, type AppointmentWithRelations } from '@vagusx/clients';
import { RescheduleDialog } from '@/scheduling/components/RescheduleDialog';
import { useCancelAppointment } from '@/scheduling/hooks/use-cancel-appointment';
import { AppointmentEhrSyncStatus } from './AppointmentEhrSyncStatus';
import { syncingRefetchInterval } from '@/lib/ehr-sync-state';

/** Active appointments (PROPOSED/PENDING/BOOKED) can be cancelled or rescheduled. */
function isActionable(status: number): boolean {
  return status === 0 || status === 1 || status === 2;
}

interface AppointmentDetailSheetProps {
  appointmentId: string | null;
  onClose: () => void;
}

/** Map numeric status to display info */
function getStatusDisplay(status: number): { label: string; color: string } {
  switch (status) {
    case 0: // PROPOSED
    case 1: // PENDING
    case 2: // BOOKED
      return {
        label: 'Booked',
        color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
      };
    case 3: // ARRIVED
      return {
        label: 'Confirmed',
        color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
      };
    case 4: // FULFILLED
      return {
        label: 'Completed',
        color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
      };
    case 5: // CANCELLED
      return {
        label: 'Cancelled',
        color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
      };
    case 6: // NOSHOW
      return {
        label: 'No Show',
        color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
      };
    default:
      return {
        label: 'Booked',
        color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
      };
  }
}

/** Get initials from first + last name */
function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

export function AppointmentDetailSheet({
  appointmentId,
  onClose,
}: AppointmentDetailSheetProps): React.ReactElement {
  const { data: appointment, isLoading } = useQuery({
    queryKey: queryKeys.appointments.detail(appointmentId ?? ''),
    queryFn: () => getAppointmentClient().getById(appointmentId!),
    enabled: !!appointmentId,
    // Watch a re-sync land (ADR-284).
    refetchInterval: (query) => syncingRefetchInterval(query.state.data ? [query.state.data] : []),
  });

  return (
    <Sheet open={!!appointmentId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex w-full max-w-md flex-col p-0 sm:max-w-lg">
        {isLoading ? (
          <DetailSkeleton />
        ) : appointment ? (
          <DetailContent appointment={appointment} onClose={onClose} />
        ) : (
          <div className="p-6">
            <SheetHeader>
              <SheetTitle>Appointment not found</SheetTitle>
              <SheetDescription>This appointment may have been deleted or moved.</SheetDescription>
            </SheetHeader>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function DetailContent({
  appointment,
  onClose,
}: {
  appointment: AppointmentWithRelations;
  onClose: () => void;
}): React.ReactElement {
  const statusDisplay = getStatusDisplay(appointment.status);
  const actionable = isActionable(appointment.status);
  const startTime = parseISO(appointment.startTime);
  const endTime = parseISO(appointment.endTime);
  const durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / 60000);
  // Show the date/time in the location's timezone (matching the EHR/practice),
  // not the viewer's browser timezone.
  const tz = appointment.location?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { isCancelling, cancel } = useCancelAppointment();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [reason, setReason] = useState('');

  const handleConfirmCancel = async (): Promise<void> => {
    const ok = await cancel(appointment.id, reason);
    if (ok) {
      // Success: the appointment is gone from this view, so close the whole detail
      // sheet (which unmounts this confirm dialog too) rather than leaving it open.
      onClose();
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <SheetBody className="px-0 pb-0">
        {/* Hero header with patient info */}
        <div className="bg-linear-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 px-6 pt-8 pb-6">
          <div className="flex items-start gap-4">
            {/* Patient avatar */}
            {appointment.patient ? (
              <div className="shrink-0 h-14 w-14 rounded-full bg-linear-to-br from-primary/80 to-primary flex items-center justify-center shadow-md">
                <span className="text-lg font-bold text-primary-foreground">
                  {getInitials(appointment.patient.firstName, appointment.patient.lastName)}
                </span>
              </div>
            ) : (
              <div className="shrink-0 h-14 w-14 rounded-full bg-muted flex items-center justify-center">
                <User className="h-6 w-6 text-muted-foreground" />
              </div>
            )}

            <div className="flex-1 min-w-0 pt-0.5">
              <h2 className="text-xl font-semibold tracking-tight truncate">
                {appointment.patient
                  ? `${appointment.patient.firstName} ${appointment.patient.lastName}`
                  : 'Unknown Patient'}
              </h2>
              {appointment.patient?.phone && (
                <div className="flex items-center gap-1.5 mt-1 text-sm text-muted-foreground">
                  <Phone className="h-3.5 w-3.5" />
                  <span>{formatPhone(appointment.patient.phone)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Status badge + EHR sync status (ADR-284) */}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Badge className={cn('text-xs font-medium px-3 py-1 border-0', statusDisplay.color)}>
              {statusDisplay.label}
            </Badge>
            <AppointmentEhrSyncStatus appointment={appointment} />
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Date & Time Card */}
          <Section>
            <SectionHeader icon={Calendar} title="Date & Time" />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <InfoCard label="Date" value={formatInTimeZone(startTime, tz, 'EEE, MMM d, yyyy')} />
              <InfoCard
                label="Time"
                value={`${formatInTimeZone(startTime, tz, 'h:mm a')} - ${formatInTimeZone(endTime, tz, 'h:mm a')}`}
              />
              <InfoCard label="Duration" value={`${durationMinutes} min`} />
              {appointment.isTelehealth && (
                <InfoCard
                  label="Type"
                  value="Telehealth"
                  icon={<Video className="h-3.5 w-3.5 text-blue-500" />}
                />
              )}
            </div>
          </Section>

          <Separator className="bg-border/50" />

          {/* Provider & Location */}
          <Section>
            <SectionHeader icon={Stethoscope} title="Provider & Location" />
            <div className="space-y-3 mt-3">
              {appointment.provider && (
                <div className="flex items-center gap-3">
                  <div className="shrink-0 h-9 w-9 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                    <Stethoscope className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      {appointment.provider.firstName} {appointment.provider.lastName}
                    </p>
                    {appointment.provider.specialty && (
                      <p className="text-xs text-muted-foreground">
                        {appointment.provider.specialty}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {appointment.location && (
                <div className="flex items-center gap-3">
                  <div className="shrink-0 h-9 w-9 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    <MapPin className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{appointment.location.name}</p>
                    {appointment.location.address && (
                      <p className="text-xs text-muted-foreground">
                        {appointment.location.address}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Section>

          {/* Visit Details */}
          {(appointment.reasonForVisit || appointment.notes) && (
            <>
              <Separator className="bg-border/50" />
              <Section>
                <SectionHeader icon={FileText} title="Visit Details" />
                <div className="space-y-3 mt-3">
                  {appointment.reasonForVisit && (
                    <DetailRow label="Reason for Visit" value={appointment.reasonForVisit} />
                  )}
                  {appointment.notes && <DetailRow label="Notes" value={appointment.notes} />}
                </div>
              </Section>
            </>
          )}

          {/* Additional Info */}
          {appointment.confirmationNumber && (
            <>
              <Separator className="bg-border/50" />
              <Section>
                <SectionHeader icon={Activity} title="Details" />
                <div className="space-y-3 mt-3">
                  {appointment.confirmationNumber && (
                    <div className="flex items-center gap-2 text-sm">
                      <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground">Confirmation:</span>
                      <span className="font-mono text-xs">{appointment.confirmationNumber}</span>
                    </div>
                  )}
                  {appointment.isTelehealth && appointment.telehealthUrl && (
                    <div className="flex items-center gap-2 text-sm">
                      <Video className="h-3.5 w-3.5 text-blue-500" />
                      <a
                        href={appointment.telehealthUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-xs truncate"
                      >
                        Join telehealth session
                      </a>
                    </div>
                  )}
                </div>
              </Section>
            </>
          )}
        </div>
      </SheetBody>

      {actionable && (
        <SheetActions
          className="sm:px-6"
          start={
            <Button variant="destructive" onClick={() => setCancelOpen(true)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Cancel appointment
            </Button>
          }
        >
          <Button onClick={() => setRescheduleOpen(true)}>
            <CalendarClock className="h-4 w-4 mr-2" />
            Reschedule
          </Button>
        </SheetActions>
      )}

      {/* Cancel confirmation */}
      <AlertDialog
        open={cancelOpen}
        onOpenChange={(v) => !v && !isCancelling && setCancelOpen(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this appointment?</AlertDialogTitle>
            <AlertDialogDescription>
              This cancels the appointment and syncs the cancellation to the EHR. It can&apos;t be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="cancel-reason" className="text-xs text-muted-foreground">
              Reason (optional)
            </Label>
            <Textarea
              id="cancel-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. patient request"
              rows={2}
              maxLength={CANCEL_REASON_MAX_CHARS}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancelling}>Keep appointment</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleConfirmCancel();
              }}
              disabled={isCancelling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isCancelling && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Cancel appointment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RescheduleDialog
        open={rescheduleOpen}
        appointment={appointment}
        onClose={() => setRescheduleOpen(false)}
        onRescheduled={onClose}
      />
    </div>
  );
}

/** Section wrapper */
function Section({ children }: { children: React.ReactNode }): React.ReactElement {
  return <div>{children}</div>;
}

/** Section header with icon */
function SectionHeader({
  icon: Icon,
  title,
}: {
  icon: React.ElementType;
  title: string;
}): React.ReactElement {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
        {title}
      </h3>
    </div>
  );
}

/** Small info card for date/time grid */
function InfoCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="rounded-lg bg-muted/50 px-3 py-2">
      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
        {label}
      </p>
      <div className="flex items-center gap-1.5">
        {icon}
        <p className="text-sm font-medium">{value}</p>
      </div>
    </div>
  );
}

/** Label + value row */
function DetailRow({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div className="rounded-lg bg-muted/30 px-3 py-2.5">
      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1">
        {label}
      </p>
      <p className="text-sm leading-relaxed">{value}</p>
    </div>
  );
}

/** Loading skeleton */
function DetailSkeleton(): React.ReactElement {
  return (
    <div className="flex flex-col">
      <div className="bg-muted/30 px-6 pt-8 pb-6">
        <div className="flex items-start gap-4">
          <Skeleton className="h-14 w-14 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-28" />
          </div>
        </div>
        <Skeleton className="h-6 w-20 mt-4 rounded-full" />
      </div>
      <div className="px-6 py-5 space-y-5">
        <div className="space-y-3">
          <Skeleton className="h-4 w-24" />
          <div className="grid grid-cols-2 gap-3">
            <Skeleton className="h-14 rounded-lg" />
            <Skeleton className="h-14 rounded-lg" />
            <Skeleton className="h-14 rounded-lg" />
          </div>
        </div>
        <Skeleton className="h-px w-full" />
        <div className="space-y-3">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-12 w-full rounded-lg" />
          <Skeleton className="h-12 w-full rounded-lg" />
        </div>
      </div>
    </div>
  );
}
